import { useEffect, useState } from "react";
import { Copy, Loader2, RefreshCw, ShieldAlert, ShieldCheck, Sparkles, UserMinus } from "lucide-react";
import { toast } from "sonner";
import { Nav } from "@/components/Nav";
import { LockedAI, SectionLabel } from "@/components/Status";
import { useAuth } from "@/context/AuthContext";
import { LOCALE, useLang } from "@/i18n";
import { api, errCode } from "@/lib/api";

export default function OrgSettings() {
  const { user, refresh } = useAuth();
  const { t, lang } = useLang();
  const [org, setOrg] = useState(null);
  const [name, setName] = useState("");

  const load = () => api.get("/org").then((r) => { setOrg(r.data); setName(r.data.name); }).catch((e) => toast.error(t(`errors.${errCode(e)}`, errCode(e))));
  const setRetention = async (days) => {
    await api.patch("/org", { retention_days: Number(days) });
    toast.success(t("common.save"));
    await load();
  };
  const giveAiConsent = async () => {
    if (!window.confirm(t("org.aiConsentConfirm"))) return;
    try {
      await api.post("/org/ai-consent");
      toast.success(t("org.aiConsentDone"));
      await Promise.all([load(), refresh()]);
    } catch (e) { toast.error(t(`errors.${errCode(e)}`, errCode(e))); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const saveName = async () => {
    await api.patch("/org", { name });
    toast.success(t("common.save"));
    await load();
    await refresh();
  };
  const regen = async () => { await api.post("/org/invite/regenerate"); await load(); };
  const setRole = async (m, role) => { await api.patch(`/org/members/${m.id}`, { role }); await load(); };
  const remove = async (m) => { if (!window.confirm(t("common.confirmDelete"))) return; await api.delete(`/org/members/${m.id}`); await load(); };
  const copy = () => { navigator.clipboard.writeText(org.invite_code); toast.success(t("common.copied")); };

  return (
    <div className="min-h-screen" data-testid="org-settings-page">
      <Nav />
      <main className="mx-auto max-w-6xl px-6 py-12">
        <div className="reveal mb-12">
          <div className="mono-label mb-3">{t("org.title")}</div>
          <h1 className="text-4xl font-semibold tracking-tight text-vox-text sm:text-5xl" data-testid="org-name-heading">{org?.name || "…"}</h1>
        </div>
        {!org ? <div className="flex items-center gap-2 text-sm text-vox-dim"><Loader2 size={14} className="animate-spin" />{t("common.loading")}</div> : (
          <div className="grid gap-10 lg:grid-cols-[1fr_360px]">
            <section className="reveal reveal-1 min-w-0">
              <SectionLabel index="01">{t("org.members")}</SectionLabel>
              <div className="overflow-x-auto rounded-2xl border border-vox-line">
                <table className="w-full text-sm" data-testid="org-members-table">
                  <thead className="bg-vox-deep/60 font-mono text-[11px] uppercase tracking-wider text-vox-dim">
                    <tr><th className="px-4 py-3 text-left font-medium">{t("auth.name")}</th><th className="px-4 py-3 text-left font-medium">{t("auth.email")}</th><th className="px-4 py-3 text-left font-medium">{t("org.role")}</th><th className="px-4 py-3" /></tr>
                  </thead>
                  <tbody>
                    {org.members.map((m) => (
                      <tr key={m.id} className="border-t border-vox-line" data-testid="org-member-row">
                        <td className="px-4 py-3 text-vox-text">{m.name}</td>
                        <td className="px-4 py-3 text-vox-muted">{m.email}</td>
                        <td className="px-4 py-3">
                          {org.can_manage && m.role !== "platform_admin" && m.id !== user.id ? (
                            <select value={m.role} onChange={(e) => setRole(m, e.target.value)} className="input !w-auto !py-1.5 text-xs" data-testid="member-role-select">
                              <option value="member">{t("org.roles.member")}</option>
                              <option value="org_admin">{t("org.roles.org_admin")}</option>
                            </select>
                          ) : <span className="font-mono text-[11px] uppercase tracking-wider text-vox-accent">{t(`org.roles.${m.role}`)}</span>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {org.can_manage && m.role !== "platform_admin" && m.id !== user.id && (
                            <button type="button" onClick={() => remove(m)} className="rounded-full p-2 text-vox-dim transition-colors hover:bg-vox-rec/10 hover:text-[#ff9b9e]" title={t("org.remove")} data-testid="member-remove-button"><UserMinus size={14} /></button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <aside className="reveal reveal-2 min-w-0 space-y-6">
              <SectionLabel index="02">{t("org.invite")}</SectionLabel>
              <div className="panel p-6">
                <p className="mb-4 text-sm text-vox-muted">{t("org.inviteHint")}</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 rounded-xl border border-vox-line bg-vox-deep/80 px-4 py-2.5 font-mono text-lg tracking-[0.2em] text-vox-bright" data-testid="org-invite-code">{org.invite_code}</code>
                  <button type="button" onClick={copy} className="btn-ghost !px-3 !py-2.5" data-testid="copy-invite-code-button"><Copy size={14} /></button>
                  {org.can_manage && <button type="button" onClick={regen} className="btn-ghost !px-3 !py-2.5" title={t("org.regenerate")} data-testid="regenerate-invite-button"><RefreshCw size={14} /></button>}
                </div>
              </div>
              {org.can_manage && (
                <div className="panel p-6">
                  <label className="mono-label mb-2 block">{t("org.name")}</label>
                  <div className="flex gap-2">
                    <input className="input" value={name} onChange={(e) => setName(e.target.value)} data-testid="org-name-input" />
                    <button type="button" onClick={saveName} className="btn-primary" data-testid="org-name-save-button">{t("common.save")}</button>
                  </div>
                </div>
              )}
              <div className="panel p-6" data-testid="org-retention-panel">
                <label className="mono-label mb-2 block" htmlFor="org-retention">{t("org.retention")}</label>
                <select id="org-retention" className="input" value={org.retention_days || 0} disabled={!org.can_manage}
                  onChange={(e) => setRetention(e.target.value)} data-testid="org-retention-select">
                  <option value={0}>{t("org.retentionUnlimited")}</option>
                  {[30, 90, 180, 365, 1095].map((d) => <option key={d} value={d}>{d} {t("org.days")}</option>)}
                </select>
                <p className="mt-3 text-xs leading-relaxed text-vox-dim">{t("org.retentionHint")}</p>
              </div>
              {org.ai_enabled ? (
                <div className={`panel p-6 ${org.ai_external_consent_at ? "border-emerald-500/30" : "border-vox-amber/40"}`} data-testid="org-ai-enabled">
                  <div className="mb-2 flex items-center gap-2 text-emerald-300"><Sparkles size={14} /><span className="mono-label !text-emerald-300">{t("org.aiTitle")}</span></div>
                  <p className="text-sm text-vox-muted">{t("org.aiOnDesc")}</p>
                  <div className="mt-5 border-t border-vox-line pt-4" data-testid="org-ai-consent-panel">
                    {org.ai_external_consent_at ? (
                      <div className="flex items-start gap-2 text-xs leading-relaxed text-vox-muted" data-testid="org-ai-consent-given">
                        <ShieldCheck size={14} className="mt-0.5 shrink-0 text-emerald-300" />
                        <span>{t("org.aiConsentGiven")} {new Date(org.ai_external_consent_at).toLocaleString(LOCALE[lang])} · {org.ai_external_consent_email}</span>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start gap-2 text-xs leading-relaxed text-vox-amber"><ShieldAlert size={14} className="mt-0.5 shrink-0" /><span className="font-medium">{t("org.aiConsentTitle")}</span></div>
                        <p className="mt-2 text-xs leading-relaxed text-vox-muted">{t("org.aiConsentHint")}</p>
                        {org.can_manage ? (
                          <button type="button" onClick={giveAiConsent} className="btn-primary mt-4 w-full" data-testid="org-ai-consent-button">{t("org.aiConsentBtn")}</button>
                        ) : <p className="mt-3 text-xs text-vox-dim">{t("org.aiConsentAdminOnly")}</p>}
                      </>
                    )}
                  </div>
                </div>
              ) : <LockedAI compact />}
            </aside>
          </div>
        )}
      </main>
    </div>
  );
}
